// South African Rand display + parsing. The format is
// `'R ' + Math.round(n).toLocaleString('en-ZA')`, which yields e.g.
// `R 1 234 567` — a leading `R `, non-breaking-space thousands separators
// (en-ZA locale, U+00A0) and no decimals for whole values.

/**
 * Format a numeric Rand value for display, e.g. `R 1 234 567`.
 * Nullish or NaN values return '' (nothing to show); 0 returns `R 0`.
 */
export function formatRand(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  return 'R ' + Math.round(value).toLocaleString('en-ZA');
}

/**
 * Compact Rand for DISPLAY ONLY, e.g. `R 367.70bn`, `R 48.2m`, `R 640k`.
 *
 * A thirteen-digit exposure figure ("R 367 696 689 538") is unreadable in a KPI
 * tile; this abbreviates to billions / millions / thousands the way the design
 * mockup does. Amounts under R 1 000 fall through to the exact {@link formatRand}.
 *
 * NOT a substitute for {@link formatRand}: it is lossy and has no inverse, so it
 * must never touch the export / import path — `parseRand` could not reconstruct
 * the original. Use it purely for at-a-glance dashboard figures.
 *
 * Trailing zeros in the mantissa are trimmed (`R 5.00bn` → `R 5bn`), so round
 * values read cleanly.
 */
export function formatRandCompact(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  const sign = value < 0 ? '-' : '';
  const n = Math.abs(value);
  const scaled = (divisor: number, decimals: number, suffix: string): string => {
    const fixed = (n / divisor).toFixed(decimals);
    // Trim trailing zeros in the FRACTION only (5.00 → 5, 48.20 → 48.2, keep
    // 48.25) — never touch an integer mantissa like "640" (that lost its 0).
    const mantissa = decimals > 0 ? fixed.replace(/\.?0+$/, '') : fixed;
    return `${sign}R ${mantissa}${suffix}`;
  };
  if (n >= 1e9) return scaled(1e9, 2, 'bn');
  if (n >= 1e6) return scaled(1e6, 1, 'm');
  if (n >= 1e3) return scaled(1e3, 0, 'k');
  return formatRand(value);
}

/**
 * Inverse of {@link formatRand}: strip the `R` prefix, spaces (regular and
 * non-breaking) and comma separators, then parse to a number. Both `,` and `.`
 * are accepted as the decimal point (en-ZA value strings carry a comma
 * decimal). Returns null if the result is not a finite number.
 */
export function parseRand(display: string): number | null {
  if (typeof display !== 'string') return null;
  let cleaned = display
    .replace(/r/gi, '')
    // strip regular spaces, non-breaking spaces and any other whitespace
    .replace(/\s/g, '');
  if (cleaned === '') return null;
  // Treat a comma as the decimal point ONLY when it is followed by exactly two
  // digits at the end of the number (e.g. "1234,56" -> 1234.56). Otherwise the
  // commas are thousands separators and are stripped — so "12,500" -> 12500 and
  // "1,234,567.89" keeps its dot decimal.
  if (/^-?\d*,\d{2}$/.test(cleaned)) {
    cleaned = cleaned.replace(',', '.');
  } else {
    cleaned = cleaned.replace(/,/g, '');
  }
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}
